import { useMutation, useQueryClient } from '@tanstack/react-query';
import { can } from '@pl/domain';
import type { DeviceDto, SiteDto } from '@pl/domain';
import { type ClipboardEvent, type FormEvent, Fragment, type KeyboardEvent, type MouseEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { zonedDateTime } from '../../lib/format';
import { qk, useDevices, useMe, useSites } from '../../lib/queries';
import { Chip, Empty, ErrorNote, RefetchNote, SiteTag, Spinner } from '../../ui/bits';
import { Modal } from '../../ui/Dialog';
import { Icon } from '../../ui/Icon';
import s from './SettingsDevices.module.css';

/** The TV's pairing code: six digits, as `POST /devices/pairing-codes` hands them out. */
const DIGITS = 6;
/** A TV's stream re-checks its device every 30 s, and each check counts as a sighting (at most one a minute). */
const ON_NOW_MS = 3 * 60_000;

/** "Just now", "12 min ago", "3 h ago", then the date on the TV's site clock: when a TV last asked the server for anything. */
function seenAgo(iso: string, now: number, timeZone: string | undefined): string {
  const mins = Math.floor((now - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)} h ago`;
  return zonedDateTime(iso, timeZone);
}

/** Ticks once a minute, so "3 min ago" does not stay on screen for an hour. */
function useMinute(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/** `mockups/office-10-settings-devices.html`: Settings → Devices, the company's TVs and pairing a new one. */
export function SettingsDevices() {
  const meQuery = useMe();
  const me = meQuery.data;
  const canManage = me ? can(me.role, 'settings') : false;
  const devicesQuery = useDevices(canManage);
  const sitesQuery = useSites();
  const [pairing, setPairing] = useState(false);
  const [revoking, setRevoking] = useState<DeviceDto | null>(null);
  const siteById = useMemo(() => new Map((sitesQuery.data ?? []).map((x) => [x.id, x])), [sitesQuery.data]);
  const now = useMinute();

  if (!me) return <Spinner label="Loading devices" />;
  if (!canManage)
    return (
      <div className="panel">
        <Empty title="Only an admin can pair TVs" description="An admin pairs each TV to one site here, and can revoke it at any time." />
      </div>
    );

  // Active first, each group in the order they were paired: a revoked TV is history.
  const devices = [...(devicesQuery.data ?? [])].sort((x, y) => Number(x.revokedAt !== null) - Number(y.revokedAt !== null));
  const activeCount = devices.filter((d) => d.revokedAt === null).length;

  return (
    <div className="panel">
      <div className="panel-h">
        <h2>TV devices</h2>
        {devicesQuery.data && (
          <Chip>
            {activeCount} paired{devices.length > activeCount ? ` · ${devices.length - activeCount} revoked` : ''}
          </Chip>
        )}
        <span className="grow" />
        <span className="sub">Each TV shows one site’s wall, read-only</span>
        <button type="button" className="btn primary sm" onClick={() => setPairing(true)}>
          <Icon name="plus" />
          Pair a TV
        </button>
      </div>

      <RefetchNote queries={[devicesQuery]} className={s.dvNote} />
      {devicesQuery.isLoadingError ? (
        <div className={s.dvNote}>
          <ErrorNote error={devicesQuery.error} />
          <button type="button" className="btn" onClick={() => devicesQuery.refetch()}>
            Try again
          </button>
        </div>
      ) : devicesQuery.isPending ? (
        <div className="panel-b">
          <Spinner label="Loading devices" />
        </div>
      ) : devices.length === 0 ? (
        <Empty
          title="No TVs yet"
          description="On the TV, open this app’s /wall address. It shows a 6-digit code: press Pair a TV and type it here."
        />
      ) : (
        <table className={`t ${s.dvTable}`}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Site</th>
              <th>Kind</th>
              <th>Paired</th>
              <th>Last seen</th>
              <th>Status</th>
              <th className="r">
                <span className={s.dvHidden}>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => {
              const revoked = d.revokedAt !== null;
              const onNow = !revoked && d.lastSeenAt !== null && now - new Date(d.lastSeenAt).getTime() < ON_NOW_MS;
              // On the TV's own site clock, named, whoever is looking: the same as Box lookup.
              const tz = siteById.get(d.siteId)?.timezone;
              return (
                <tr key={d.id} className={revoked ? s.dvOff : undefined}>
                  <td>
                    <div className={s.dvName}>
                      <span className={s.dvIcon} aria-hidden="true">
                        <Icon name="tv" />
                      </span>
                      <b>{d.name}</b>
                    </div>
                  </td>
                  <td>
                    <SiteTag site={siteById.get(d.siteId)} />
                  </td>
                  <td>TV wall</td>
                  <td className="muted">{zonedDateTime(d.pairedAt, tz)}</td>
                  <td>
                    {d.lastSeenAt === null ? (
                      <span className="muted">Never</span>
                    ) : (
                      <span className={s.dvSeen} title={zonedDateTime(d.lastSeenAt, tz)}>
                        {onNow && <i aria-hidden="true" />}
                        {seenAgo(d.lastSeenAt, now, tz)}
                      </span>
                    )}
                  </td>
                  <td>
                    {revoked ? (
                      <Chip>Revoked</Chip>
                    ) : (
                      <Chip tone="ok" dot>
                        Active
                      </Chip>
                    )}
                  </td>
                  <td className="r">
                    {!revoked && (
                      <button type="button" className="btn sm danger" aria-label={`Revoke ${d.name}`} onClick={() => setRevoking(d)}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {pairing && <PairModal sites={me.sites} onClose={() => setPairing(false)} />}
      {revoking && <RevokeModal device={revoking} onClose={() => setRevoking(null)} />}
    </div>
  );
}

/**
 * What went wrong with a pairing, in words an admin can act on. The claim limits (per admin and per
 * company) both answer 429; which one it was does not change what to do.
 */
function pairError(err: unknown): unknown {
  if (!(err instanceof ApiError)) return err;
  if (err.status === 404) return new Error('That code isn’t valid or has expired. Check the code on the TV and type it again.');
  if (err.status === 429) return new Error('Too many tries. Wait a minute and try again.');
  return err; // 409 "already used", 400 "Unknown site": the server's own words say what to do
}

/**
 * Six single-digit boxes. Typing moves to the next box, Backspace on an empty box goes back one,
 * and a pasted code ("482913", "482 913") fills the boxes from where it lands, or all of them when it
 * is a whole code.
 */
function CodeBoxes({ id, labelId, value, onChange }: { id: string; labelId: string; value: string[]; onChange: (digits: string[]) => void }) {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  const focus = (i: number) => {
    const el = boxes.current[Math.max(0, Math.min(DIGITS - 1, i))];
    el?.focus();
    el?.select();
  };

  /** Writes `text`'s digits from box `at` on, then moves to the box after the last one written. */
  function fill(at: number, text: string) {
    const digits = text.replace(/\D/g, '').slice(0, DIGITS - at).split('');
    if (digits.length === 0) return;
    const next = [...value];
    digits.forEach((d, k) => (next[at + k] = d));
    onChange(next);
    focus(at + digits.length);
  }

  function onKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const at = value[i] ? i : i - 1;
      if (at < 0) return;
      const next = [...value];
      next[at] = '';
      onChange(next);
      focus(at);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focus(i - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focus(i + 1);
    }
  }

  function onPaste(i: number, e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '');
    fill(digits.length >= DIGITS ? 0 : i, digits);
  }

  // A click lands on the first empty box: the code is typed left to right, and a box further on
  // would take the digits in the wrong places. A filled box can still be clicked, to change it.
  const firstEmpty = value.findIndex((d) => d === '');
  const land = (e: MouseEvent<HTMLElement>, clicked: number | null) => {
    const target = firstEmpty === -1 ? (clicked ?? DIGITS - 1) : clicked === null || clicked > firstEmpty ? firstEmpty : clicked;
    if (target === clicked) return;
    e.preventDefault();
    focus(target);
  };

  return (
    // The gaps between boxes are part of the field too.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={s.dvDigits} role="group" aria-labelledby={labelId} onMouseDown={(e) => land(e, null)}>
      {value.map((d, i) => (
        <Fragment key={i}>
          {i === DIGITS / 2 && <i className={s.dvGap} aria-hidden="true" />}
          <input
            ref={(el) => {
              boxes.current[i] = el;
            }}
            id={i === 0 ? id : undefined}
            className={`mono ${s.dvDigit}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            autoFocus={i === 0}
            aria-label={`Digit ${i + 1} of ${DIGITS}`}
            value={d}
            onMouseDown={(e) => {
              e.stopPropagation();
              land(e, i);
            }}
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              // Emptied (Delete, cut): clear this box. Anything else is typed or dropped digits; a
              // letter has none, so the box keeps what it had.
              if (e.target.value === '') onChange(value.map((v, k) => (k === i ? '' : v)));
              else fill(i, e.target.value);
            }}
            onKeyDown={(e) => onKeyDown(i, e)}
            onPaste={(e) => onPaste(i, e)}
          />
        </Fragment>
      ))}
    </div>
  );
}

/** `mockups/office-10-settings-devices.html`'s Pair a TV modal: the code the TV shows, its site and a name. */
function PairModal({ sites, onClose }: { sites: SiteDto[]; onClose: () => void }) {
  const qc = useQueryClient();
  const id = useId();
  const [digits, setDigits] = useState<string[]>(() => Array.from({ length: DIGITS }, () => ''));
  // One site to choose from is chosen already; with more, the admin says which, deliberately.
  const [siteId, setSiteId] = useState(sites.length === 1 ? sites[0]!.id : '');
  const [name, setName] = useState('');
  const code = digits.join('');
  const site = sites.find((x) => x.id === siteId);

  const pair = useMutation({
    mutationFn: () => api.post<DeviceDto>('/devices/pair', { code, siteId, name: name.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices });
      onClose();
    },
  });

  const canPair = code.length === DIGITS && Boolean(site) && Boolean(name.trim()) && !pair.isPending;
  // What Pair TV is waiting for, said beside it: a greyed-out button with no reason reads as broken.
  const missing =
    code.length < DIGITS
      ? `Type all 6 digits (${code.length} of 6)`
      : !site
        ? 'Choose the site'
        : !name.trim()
          ? 'Give the TV a name'
          : null;

  function edited() {
    // An error names the form as it was sent; once it changes, the error is about something else.
    if (pair.isError) pair.reset();
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (canPair) pair.mutate();
  }

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Pair a TV"
      footer={
        <>
          <span className="sub" id={`${id}-why`}>
            {missing ?? 'Admin only'}
          </span>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={`${id}-form`} className="btn primary" disabled={!canPair} aria-describedby={`${id}-why`}>
            <Icon name="link" />
            Pair TV
          </button>
        </>
      }
    >
      <form id={`${id}-form`} className={s.dvForm} onSubmit={submit} noValidate>
        <p className={s.dvIntro}>
          On the TV, open this app’s <span className="mono">/wall</span> address. It shows a 6-digit code.
        </p>
        {pair.isError && <ErrorNote error={pairError(pair.error)} />}
        <div className="fld">
          <label id={`${id}-code-l`} htmlFor={`${id}-code`}>
            Pairing code
          </label>
          <CodeBoxes
            id={`${id}-code`}
            labelId={`${id}-code-l`}
            value={digits}
            onChange={(d) => {
              setDigits(d);
              edited();
            }}
          />
        </div>
        <div className={s.dvTwo}>
          <div className="fld">
            <label htmlFor={`${id}-site`}>Site</label>
            <select
              id={`${id}-site`}
              className={`inp ${s.dvSelect}`}
              value={siteId}
              onChange={(e) => {
                setSiteId(e.target.value);
                edited();
              }}
            >
              {sites.length !== 1 && (
                <option value="" disabled>
                  Choose a site
                </option>
              )}
              {sites.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>
          <div className="fld">
            <label htmlFor={`${id}-name`}>Name</label>
            <input
              id={`${id}-name`}
              className="inp"
              value={name}
              maxLength={80}
              placeholder="CA dock TV"
              autoComplete="off"
              onChange={(e) => {
                setName(e.target.value);
                edited();
              }}
            />
          </div>
        </div>
        <p className={s.dvLock}>
          <Icon name="lock" />
          <span>{site ? `The TV gets a read-only token for ${site.name} only.` : 'The TV gets a read-only token for one site only.'} Revoke it here at any time.</span>
        </p>
      </form>
    </Modal>
  );
}

/** Revoke one TV, behind a line that names it and says what the TV will do. */
function RevokeModal({ device, onClose }: { device: DeviceDto; onClose: () => void }) {
  const qc = useQueryClient();
  const revoke = useMutation({
    mutationFn: () => api.post<DeviceDto>(`/devices/${device.id}/revoke`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.devices });
      onClose();
    },
  });
  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={`Revoke ${device.name}?`}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn danger" disabled={revoke.isPending} onClick={() => revoke.mutate()}>
            Revoke
          </button>
        </>
      }
    >
      {revoke.isError && <ErrorNote error={revoke.error} />}
      <p className={s.dvConfirm}>It will go back to the pairing screen.</p>
      <p className={s.dvConfirmSub}>To use it again, pair it with the new code it shows.</p>
    </Modal>
  );
}
